import { ReactElement } from 'react';
import { Anchor } from '@twilio-paste/core/anchor';
import { Box } from '@twilio-paste/core/box';
import { Callout, CalloutHeading } from '@twilio-paste/core/callout';

import { useSegment } from '@twiliointernal/e2-commons-shared-hooks';
import { logger } from '@twiliointernal/e2-commons-shared-libs';

import { copy, constants } from '../definitions';
import { RenderWelcomeBannerProps } from '../definitions/types';
import { useCurrentPlan, useUserDetails } from '../../../hooks';
import { trackingDetails } from '../../../definitions/constants';
import { isPersonalizedSupport } from '../../../providers/supportPlanProvider/utils';

const WelcomeBanner = (): ReactElement => {
  const currentPlan = useCurrentPlan();
  const { account, user, isLoading, isLoggedIn } = useUserDetails();
  const { trackEvent } = useSegment({
    accountSid: account?.sid,
    userSid: user?.sid,
    isLoading,
    isLoggedIn,
  });
  const isPersonalized = currentPlan?.supportPlan?.billableItemSid
    ? isPersonalizedSupport(currentPlan?.supportPlan?.billableItemSid)
    : false;

  const renderWelcomeBanner = ({
    heading,
    articleId,
    label,
  }: RenderWelcomeBannerProps): ReactElement => (
    <Box marginBottom="space60">
      <Callout variant="new">
        <CalloutHeading as="h2">{heading}</CalloutHeading>
        <Box marginTop="spaceNegative40">
          <Anchor
            target="_blank"
            href={`/articles/${articleId}`}
            onClick={(): void => {
              const eventName = trackingDetails.welcomeBannerLinkClick;
              const trackingContext = {
                articleId,
                linkLabel: label,
              };

              logger.info(
                `${constants.LOG_PREFIX_WELCOME_BANNER}::[${eventName}]`,
                trackingContext,
              );
              trackEvent(eventName, trackingContext);
            }}
          >
            {label}
          </Anchor>
        </Box>
      </Callout>
    </Box>
  );

  return isPersonalized
    ? renderWelcomeBanner({ ...copy.welcomeBanner.personalizedSupport })
    : renderWelcomeBanner({ ...copy.welcomeBanner.nonPersonalizedSupport });
};

export default WelcomeBanner;
